import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Calendar } from 'lucide-react';
import ReservationTrends from './ReservationTrends';
import GymLeaderboard from './GymLeaderboard';
import UserGrowth from './UserGrowth';
import FeatureUsage from './FeatureUsage';

const UsageAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/analytics/usage?days=${dateRange}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/analytics/export?days=${dateRange}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting analytics:', err);
      alert('Failed to export analytics');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Usage Analytics</h2>
          <p className="mt-1 text-sm text-gray-600">
            Comprehensive platform usage metrics and trends
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Date Range Selector */}
          <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm">
            <Calendar className="w-5 h-5 text-gray-400 ml-3" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="pl-2 pr-8 py-2 border-0 focus:ring-2 focus:ring-blue-500 rounded-lg text-sm"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Reservations
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {analytics?.summary?.totalReservations?.toLocaleString() || 0}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Unique Users
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {analytics?.summary?.uniqueUsers?.toLocaleString() || 0}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Avg. Daily Reservations
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {analytics?.summary?.avgDailyReservations?.toFixed(1) || 0}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Board Utilization
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {analytics?.summary?.utilizationRate?.toFixed(1) || 0}%
            </dd>
          </div>
        </div>
      </div>

      {/* Reservation Trends Chart */}
      <ReservationTrends data={analytics?.trends} />

      {/* User Growth Chart */}
      <UserGrowth data={analytics?.userGrowth} />

      {/* Gym Leaderboard */}
      <GymLeaderboard gyms={analytics?.gymStats} />

      {/* Feature Usage */}
      <FeatureUsage features={analytics?.featureUsage} />
    </div>
  );
};

export default UsageAnalytics;
